import React, { useState, useContext, useEffect } from "react";
import classNames from "classnames";
import * as Icons from "Components/Icons";
import { Rating } from "Components/Rating";
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Button, SpinningButton } from "Components/ReactstrapSlim";
import { ITeamMemberReservation, ReservationTimeType, ReservationState, HourlyRequestToBookStatus, ReservationVisitStatesClient, ReservationGuestsClient, ReservationMethod, IReservationMaybeMemberViewModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { formatReservationTime } from "Utils/datetime";
import { GuestAvatarList } from "./GuestAvatarList";
import ReviewModal from "./Reservations/ReviewModal";
import InviteGuestsModal from "./Reservations/InviteGuestsModal";
import CancelReservationModal from "./Reservations/CancelReservationModal";
import UserContext from "../UserContext";
import moment from "moment";
import { BIN } from "features/ComponentsLib";

export interface ReservationRowProps {
   reservation: ITeamMemberReservation,
   onChange: (reservation: ITeamMemberReservation) => void;
   onHourlyRequestToBookCancel: (requestId: Guid) => void;
   onHourlyRequestToBookDetails: (requestId: Guid) => void;
   onReservationDetails: (reservation: ITeamMemberReservation) => void;
   reservationType: ReservationTimeType,
   className?: string,
   onUpdateReservationList?: () => void

}
export const ReservationRow: React.FC<ReservationRowProps> = ({ reservation, reservationType, className, onHourlyRequestToBookCancel, onHourlyRequestToBookDetails, onReservationDetails, onUpdateReservationList = () => { /*empty*/ }, onChange }) => {
   const { user } = useContext(UserContext);
   const reservationVisitStatesClient = useApiClient(ReservationVisitStatesClient);
   const reservationGuestsClient = useApiClient(ReservationGuestsClient);
   const [invitees, setInvitees] = useState<IReservationMaybeMemberViewModel[]>(reservation.invitees || []);
   const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
   const [isInviteGuestsModalOpen, setIsInviteGuestsModalOpen] = useState(false);
   const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
   const [isLoading, setIsLoading] = useState(false);   

   useEffect(() => {
      const abortController = new AbortController();
      if (reservation.invitees?.length && reservationType == ReservationTimeType.Future) {
         reservationGuestsClient.get(reservation.idForLink, abortController.signal)
            .then(data => {
               setInvitees(data);
            });
      }
      else {
         setInvitees([]);
      }

      return function cleanup() {
         abortController.abort();
      }
   }, [reservation.invitees]);

   const onUpdateReservation = async () => {
      try {
         onChange(reservation);
      }
      catch (err) {
         console.log(err);
      }
   }

   const onCheckIn = async () => {
      try {
         setIsLoading(true);
         const result = await reservationVisitStatesClient.checkIn(reservation.idForLink, reservation.occurrenceDate);
         if (result.success) {
            await onUpdateReservation();
         }
      }
      catch (err) {
         console.log(err);
      }
      finally {
         setIsLoading(false);
      }
   }

   const onCheckOut = async () => {
      try {
         setIsLoading(true);
         const result = await reservationVisitStatesClient.checkOut(reservation.idForLink, reservation.occurrenceDate);
         if (result.success) {
            if (reservationType == ReservationTimeType.Past || user.isSuperUser) {
               await onUpdateReservation();
            } else {
               onUpdateReservationList();
            }
         }
      }
      catch (err) {
         console.log(err);
      }
      finally {
         setIsLoading(false);
      }
   }

   const openBIN = () => {
      BIN({
         type: "EDIT",
         reservationId: reservation.idForLink,
         occurenceDate: reservation.occurrenceDate,         
         onClose: (isUpdated) => {
            if(isUpdated)
               onUpdateReservation();
         }
      });
   }

   const showHrtbDetails = () => {
      if (reservation.hourlyRequestToBook)
         onHourlyRequestToBookDetails(reservation.hourlyRequestToBook.id);
   }

   const onHrtbCancel = () => {
      if (reservation.hourlyRequestToBook)
         onHourlyRequestToBookCancel(reservation.hourlyRequestToBook.id);
   }

   const getTimeDate = () => moment(reservation.startTime).isSame(new Date(), "year") ? moment(reservation.startTime).format("dddd, MMM D") : moment(reservation.startTime).format("dddd, MMM D 'YY");

   const getTimeHour = () => {
      return reservation.reservationMethod != ReservationMethod.Monthly
         ? <>{formatReservationTime(reservation.startTime, reservation.endTime)} {reservation.occurrenceDate ? <Icons.Loop /> : ""}</>
         : reservation.isAutoRenew
            ? "Autorenews"
            : `Ends ${moment(reservation.endTime).format("MMMM D, YYYY")}`;
   }

   return (<>
      <div className={classNames("reservation-row card", className)} onClick={() => reservation.hourlyRequestToBook != null ? showHrtbDetails() : onReservationDetails(reservation)}>
         <div className="reservation-row-image" style={{ backgroundImage: `url(${reservation.picture})` }}>
            <div className="reservation-row-badges">
               {reservation.isToday && reservationType == ReservationTimeType.Future && <div className="reservation-row-badge">Today</div>}
            </div>
         </div>
         <div className="reservation-row-content">
            <div className="reservation-row-location">
               <div className="reservation-row-location-name">{reservation.venueName}</div>
               <div className="reservation-row-location-address">{reservation.address}</div>
            </div>
            <div className="reservation-row-time">
               <div className="reservation-row-time-icon">
                  <Icons.Clock />
               </div>
               <div className="reservation-row-time-inner">
                  <div className="reservation-row-time-date">{getTimeDate()}</div>
                  <div className="delimiter">•</div>
                  <div className="reservation-row-time-hour">{getTimeHour()}</div>
               </div>
            </div>
            {
               reservationType == ReservationTimeType.Future
                  ? <>
                     {reservation.hourlyRequestToBook == null && <GuestAvatarList organizer={reservation.organizer} guests={invitees} capacity={reservation.workspaceCapacity} className={classNames("reservation-row-invitees")} />}
                     <div className={reservation.hourlyRequestToBook == null ? "reservation-row-cta" : "reservation-row-request-status"}>
                        {
                           reservation.hourlyRequestToBook != null
                              ? reservation.hourlyRequestToBook.status == HourlyRequestToBookStatus.Pending || reservation.hourlyRequestToBook.status == HourlyRequestToBookStatus.Paused
                                 ? <span className="text-ocean-blue h8">Request Pending</span>
                                 : <span className="text-danger h8">{reservation.hourlyRequestToBook.status == HourlyRequestToBookStatus.CancelledByHost ? "Declined by Host" : "Request Cancelled"}</span>
                              : reservation.deleted
                                 ? <div className="cancel-state">Cancelled</div>
                                 : reservation.reservationOperations?.checkInAllowed
                                    ? <SpinningButton color="primary" size="sm" onClick={async (e) => { e.stopPropagation(); await onCheckIn() }} isLoading={isLoading}>
                                       Check-in
                                    </SpinningButton>
                                    : reservation.reservationOperations?.checkOutAllowed
                                       ? <SpinningButton color="arctic-20" size="sm" onClick={(e) => { e.stopPropagation(); }} isLoading={isLoading}>
                                          <Icons.Check className='mr-px8' /> Arrived
                                       </SpinningButton>
                                       : reservation.reservationOperations?.inviteAllowed
                                          ? <Button color="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setIsInviteGuestsModalOpen(true); }} disabled={isLoading}><Icons.Add className="mr-px8" />Invite</Button>
                                          : null
                        }

                     </div>
                  </>
                  : <div className="reservation-row-review">
                     {
                        reservation.deleted
                           ? <div className="cancel-state">Cancelled</div>
                           : reservation.reservationState == ReservationState.Declined
                              ? <div className="unattend-state">Did not attend</div>
                              : reservation.memberStars
                                 ? <Rating stars={reservation.memberStars} className="px-px8 pointer" hideNumber onClick={(e) => { e.stopPropagation(); setIsReviewModalOpen(true) }} />
                                 : <Button size="md" className="btn-primary" onClick={(e) => { e.stopPropagation(); setIsReviewModalOpen(true); }}>Leave review</Button>
                     }
                  </div>
            }
            <div className="reservation-row-actions">
               <UncontrolledDropdown disabled={isLoading}>
                  <DropdownToggle color="link" className="rounded" size="icon" onClick={(e) => { e.stopPropagation() }}>
                     <Icons.MoreHorizontal className="icon" />
                  </DropdownToggle>
                  <DropdownMenu right>
                     {reservation.hourlyRequestToBook != null
                        ? <>
                           <DropdownItem onClick={e => { e.stopPropagation(); showHrtbDetails(); }}>View Details</DropdownItem>
                           <DropdownItem onClick={e => { e.stopPropagation(); import("LiquidSpaceEmbedded").then(x => x.default.actions["ls-messageHost"]({ venueId: reservation.venueId, workspaceId: reservation.workspaceId })); }}>Message Host</DropdownItem>
                           {(reservation.hourlyRequestToBook.status == HourlyRequestToBookStatus.Pending || reservation.hourlyRequestToBook.status == HourlyRequestToBookStatus.Paused) && <DropdownItem onClick={e => { e.stopPropagation(); onHrtbCancel() }}>Cancel Request</DropdownItem>}
                        </>
                        : <>
                           {
                              reservation.reservationOperations?.inviteAllowed
                                 ? <DropdownItem onClick={(e) => { e.stopPropagation(); setIsInviteGuestsModalOpen(true); }}>Invite guests</DropdownItem>
                                 : null
                           }
                           {
                              reservation.reservationOperations?.checkInAllowed
                                 ? <DropdownItem onClick={async (e) => { e.stopPropagation(); await onCheckIn(); }}>Check-in</DropdownItem>
                                 : null
                           }
                           {
                              reservation.reservationOperations?.checkOutAllowed
                                 ? <DropdownItem onClick={async (e) => { e.stopPropagation(); await onCheckOut(); }}>Check-out</DropdownItem>
                                 : null
                           }
                           <DropdownItem onClick={(e) => { e.stopPropagation(); onReservationDetails(reservation); }}>View Details</DropdownItem>
                           {!!reservation.costSummary && <DropdownItem onClick={e => { e.stopPropagation(); window.open(`/my/receipt/${reservation.idForLink}`, "_blank") }}>View Receipt</DropdownItem>}
                           <DropdownItem onClick={e => { e.stopPropagation(); import("LiquidSpaceEmbedded").then(x => x.default.actions["ls-messageHost"]({ venueId: reservation.venueId, workspaceId: reservation.workspaceId })) }}>Message Host</DropdownItem>
                           {
                              reservation.reservationOperations?.editAllowed
                                 ? <DropdownItem onClick={(e) => { e.stopPropagation(); openBIN() }}>Change Reservation</DropdownItem>
                                 : null
                           }
                           {
                              reservation.reservationOperations?.cancelAllowed
                                 ? <DropdownItem onClick={(e) => { e.stopPropagation(); setIsCancelModalOpen(true) }}>Cancel Reservation</DropdownItem>
                                 : null
                           }
                           <DropdownItem onClick={e => { e.stopPropagation(); window.open(reservation.workspaceUrl, "_blank") }}>Book Again</DropdownItem>
                           {/* <DropdownItem>Refund</DropdownItem>
                        <DropdownItem>Resend Confirmation</DropdownItem> */}
                           {/* <DropdownItem>DASH License®</DropdownItem> */}
                        </>}
                  </DropdownMenu>
               </UncontrolledDropdown>
            </div>
         </div>
      </div>
      {isReviewModalOpen && <ReviewModal reservation={reservation} onClosePopup={() => setIsReviewModalOpen(false)} onLeaveReview={async () => await onUpdateReservation()} />}
      {isInviteGuestsModalOpen && <InviteGuestsModal reservation={reservation} onClosePopup={() => setIsInviteGuestsModalOpen(false)} onSendInvite={async () => await onUpdateReservation()} />}
      {isCancelModalOpen && <CancelReservationModal reservation={reservation} onClose={() => setIsCancelModalOpen(false)} onSingleSuccess={async () => await onUpdateReservation()} onRecurrentSuccess={async () => await onUpdateReservationList()} />}
   </>);
}